/**
 * Zero-based nutrition foods hook.
 *
 * Provides real-time synced access to nutrition foods data.
 * Uses Zero 0.25+ queries API with server-side query resolution.
 */

import { useQuery } from "@rocicorp/zero/react";
import { useMemo } from "react";

import type {
  DataSource,
  EnrichmentStatus,
  FoodGroupCode,
  NutritionFood,
} from "@animus/habit-modules/nutrition";
import { queries, type ZeroHabitNutritionFoodRow } from "@animus/zero-schema";

interface UseZeroFoodsOptions {
  search?: string;
  foodGroup?: string;
  limit?: number;
}

interface UseZeroFoodsResult {
  foods: NutritionFood[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Safely parse a JSON array that might be a string or already parsed.
 * Zero's json() type may return strings depending on how data was inserted.
 */
function parseJsonArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Convert Zero row to domain type.
 *
 * Zero stores timestamps as epoch numbers and enums as strings.
 * We trust the database contains valid enum values.
 */
function toNutritionFood(row: ZeroHabitNutritionFoodRow): NutritionFood {
  if (!row.id) {
    throw new Error(`Food row missing ID: ${row.name}`);
  }

  return {
    id: row.id,
    name: row.name,
    normalizedName: row.normalizedName,
    description: row.description ?? null,

    calories: row.calories,
    protein: row.protein,
    fat: row.fat,
    carbs: row.carbs,
    fiber: row.fiber,
    saturatedFat: row.saturatedFat,

    // Source tracking - trust database values
    caloriesSource: row.caloriesSource as DataSource,
    caloriesSourceId: row.caloriesSourceId ?? null,
    caloriesConfidence: row.caloriesConfidence,
    proteinSource: row.proteinSource as DataSource,
    proteinSourceId: row.proteinSourceId ?? null,
    proteinConfidence: row.proteinConfidence,
    fatSource: row.fatSource as DataSource,
    fatSourceId: row.fatSourceId ?? null,
    fatConfidence: row.fatConfidence,
    carbsSource: row.carbsSource as DataSource,
    carbsSourceId: row.carbsSourceId ?? null,
    carbsConfidence: row.carbsConfidence,
    fiberSource: row.fiberSource as DataSource,
    fiberSourceId: row.fiberSourceId ?? null,
    fiberConfidence: row.fiberConfidence,
    saturatedFatSource: row.saturatedFatSource as DataSource,
    saturatedFatSourceId: row.saturatedFatSourceId ?? null,
    saturatedFatConfidence: row.saturatedFatConfidence,

    // Classification - trust database values
    foodGroup: row.foodGroup as FoodGroupCode,
    tags: parseJsonArray<string>(row.tags),
    compatibleModifiers: parseJsonArray<string>(row.compatibleModifiers),
    enrichmentStatus: (row.enrichmentStatus as EnrichmentStatus) ?? null,

    // Timestamps - convert epoch to Date
    createdAt: new Date(row.createdAt ?? Date.now()),
    updatedAt: new Date(row.updatedAt ?? Date.now()),
  };
}

/**
 * Extract error message from Zero query result.
 */
function getErrorMessage(
  queryResult: ReturnType<typeof useQuery>[1]
): string | null {
  if (queryResult.type === "error") {
    return queryResult.error.message;
  }
  return null;
}

export function useZeroFoods(
  options?: UseZeroFoodsOptions
): UseZeroFoodsResult {
  const { search, foodGroup, limit = 100 } = options ?? {};

  // Use the new Zero 0.25+ queries API
  const [rawFoods, queryResult] = useQuery(
    queries.foods.all({ search, foodGroup, limit })
  );

  const foods = useMemo(() => rawFoods?.map(toNutritionFood) ?? [], [rawFoods]);

  const error = getErrorMessage(queryResult);

  return {
    foods,
    isLoading: rawFoods === undefined,
    error,
  };
}
